import {useState} from "react"
import {Col, Radio, Row} from "antd"
import {Screen} from "components/ui"
import {useTranslation} from "react-i18next"
import styles from "./Chat.module.css"
import {ThreadStyle} from "types/chatSettings"
import {BaseButton, BaseLoading} from "components"
import {generalSettingsService} from "services"
import {GeneralSettingField} from "types/settings/general-setting"
import {handleError, toastSuccess} from "helpers"
import {YesNo} from "types/common"
import {isEmpty} from "lodash"
import {useMutation, useQuery} from "@tanstack/react-query"

export default function Chat() {
    const {t} = useTranslation(["chat", "common"])
    const [settings, setSettings] = useState({threadStyle: null, deleteOwnMessages: null})

    const settingsQ = useQuery({
        queryKey: ["chat-message-settings"],
        queryFn: () =>
            generalSettingsService.getSettings([
                GeneralSettingField.ChatThreadStyle,
                GeneralSettingField.DeleteOwnMessagesChat
            ]),
        initialData: [],
        onError: (err) => handleError(err),
        onSuccess: (settings) => {
            const chatThreadStyle = settings.find((item) => item.field === GeneralSettingField.ChatThreadStyle)
            const deleteOwnMessageSettings = settings.find(
                (item) => item.field === GeneralSettingField.DeleteOwnMessagesChat
            )
            const chatThreadStyleData = isEmpty(chatThreadStyle)
                ? ThreadStyle.Default
                : (chatThreadStyle.value as ThreadStyle)
            const deleteOwnMessageData = isEmpty(deleteOwnMessageSettings)
                ? YesNo.Yes
                : (deleteOwnMessageSettings.value as YesNo)
            setSettings({threadStyle: chatThreadStyleData, deleteOwnMessages: deleteOwnMessageData})
        }
    })

    const updateSettingsMutation = useMutation(
        () => {
            return generalSettingsService.updateSettings([
                {field: GeneralSettingField.ChatThreadStyle, value: settings.threadStyle},
                {field: GeneralSettingField.DeleteOwnMessagesChat, value: settings.deleteOwnMessages}
            ])
        },
        {onSuccess: () => toastSuccess("Successfully updated"), onError: (error) => handleError(error)}
    )

    const onChangeSetting = (key: string, value: any) => {
        setSettings((prev) => ({...prev, [key]: value}))
    }

    return (
        <Screen
            htmlTitle={t("chat:chatSettings")}
            header={{
                title: t("chat:chatSettings")
            }}>
            <div>
                <div className={styles.infoChoicesWrap}>
                    <BaseLoading isShow={settingsQ.isFetching || settingsQ.isLoading} />
                    <Row gutter={[20, 20]}>
                        <Col span={10}>
                            <p className={styles.infoTitleChoicesWrap}>{t("chat:threadStyle")}: </p>
                        </Col>
                        <Col span={14}>
                            <Radio.Group
                                className={styles.radioGroup}
                                onChange={(event) => onChangeSetting("threadStyle", event.target.value)}
                                value={settings.threadStyle}>
                                <Radio className={styles.radioBtn__item} value={ThreadStyle.Default}>
                                    {t("chat:default")}
                                </Radio>
                                <Radio className={styles.radioBtn__item} value={ThreadStyle.CommentsAbove}>
                                    {t("chat:commentsAbove")}
                                </Radio>
                            </Radio.Group>
                        </Col>
                        <Col span={10}>
                            <p className={styles.infoTitleChoicesWrap}>Delete own messages: </p>
                        </Col>
                        <Col span={14}>
                            <Radio.Group
                                className={styles.radioGroup}
                                onChange={(event) => onChangeSetting("deleteOwnMessages", event.target.value)}
                                value={settings.deleteOwnMessages}>
                                <Radio className={styles.radioBtn__item} value={YesNo.Yes}>
                                    Yes
                                </Radio>
                                <Radio className={styles.radioBtn__item} value={YesNo.No}>
                                    No
                                </Radio>
                            </Radio.Group>
                        </Col>
                    </Row>
                </div>
                <div className={styles.doneBtn}>
                    <BaseButton
                        title={t("common:action.save")}
                        onClick={updateSettingsMutation.mutate}
                        loading={updateSettingsMutation.isLoading}
                        disabled={settingsQ.isFetching || settingsQ.isLoading}
                    />
                </div>
            </div>
        </Screen>
    )
}
